import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  UncontrolledDropdown,
} from 'src/componentes/dropdown';

import http from 'src/services/httpService';
import httpSelector from 'src/services/httpSelector';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import Table from 'src/componentes/table';
import Pagination from 'src/componentes/list/pagination';
import Confirm from 'src/componentes/message-box/confirm';
import errorHandler from 'src/utils/error-handler';
import { Checkbox } from 'src/componentes/Form';
import guid from 'src/utils/guid';
import Info from 'src/componentes/info';
import LucideIcon from 'src/componentes/lucide-icon';
import {
  MIN_COLUMN_WIDTH,
  buildWidthPayload,
  computeResizedWidth,
  measureHeaderMinWidth,
  withUpdatedColumnWidth,
} from 'src/componentes/list/utils/column-resize';

// Estilos de uma célula de coluna redimensionada: o conteúdo trunca com "..." em vez de
// empurrar a largura da coluna (o que impedia encolher até o texto do cabeçalho).
const truncatedOuterStyle = { minWidth: 0, maxWidth: '100%' };
const truncatedContentStyle = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Aplica o mesmo estilo direto no DOM (preview ao vivo durante o arraste, sem
// re-renderizar): td > div externo (d-flex) > ... > div do conteúdo (último filho).
function applyCellTruncation(td, width) {
  td.style.width = `${width}px`;
  td.style.minWidth = `${width}px`;
  td.style.maxWidth = `${width}px`;
  td.style.overflow = 'hidden';

  const outer = td.firstElementChild;
  if (!outer) return;
  Object.assign(outer.style, truncatedOuterStyle);

  const content = outer.lastElementChild;
  if (content) Object.assign(content.style, truncatedContentStyle);
}

const stickyDefault = {
  position: 'sticky',
  background: 'inherit',
  zIndex: 5,
};

const EMPTY_COLUMNS = [];

const colorIcon = '#939393';

function Results({
  resources,
  count,
  columns: sourceColumns = EMPTY_COLUMNS,
  columnsTag,
  tag = columnsTag,
  viewType,
  onColumnsChange,
  showPagination = true,
  currentPage,
  pageSize,
  onChangeCurrentPage,
  onChangePageSize,
  onSortColumn,
  onSortByColumn,
  forceShowSort = false,
  sort,
  sortBy,
  data = [],
  url /** Caminho para Controller, com '/' no início */,
  updatedItems = [],
  showDelete = true,
  parameters,
  onRefresh,
  handleRemove,
  onRemove,
  handleEditClick,
  showMenu,
  expandedIds,
  loadingChildrenIds,
  isExpanded,
  handleToggleExpand,
  select = false,
  selectLimit,
  onSelect,
  onDeleting,
  responsive = true,
  sticky = false,
  selectedItems = [],
  getRowColor = () => {},
  getRowTextColor = () => {},
  renderFakeChildrenWhileLoading,
  renderContentWhileLoading,
  renderMenu: renderMenuProps,
  showSortIcon = true,
  isLoading = false,
  dragTable = false,
  showFullTextColumns = false,
  multiple = false,
  idLabelEditar,
  idLabelExcluir,
  showScrollColumn = true,
  propsPopOver = {},
  handleRefreshPage,
  dataTotalRow,
  selectedCallback,
  checkSelected = false,
  showSelectAll = true,
  onColumnResize,
  columnResizeEnabled: resizeEnabled,
  disableColumnResize = false,
}) {
  const intl = useIntl();
  const [allItensSelected, setAllItensSelected] = useState(false);
  const [currentSelectedItems, setCurrentSelectedItems] = useState([
    ...(selectedItems || []),
  ]);

  const [columnWidths, setColumnWidths] = useState({
    tag,
    viewType,
    values: new Map(),
  });
  const pendingColumnWidthSaveRef = useRef(null);
  const lastColumnWidthSaveRef = useRef(null);
  const columnResizeEnabled =
    !disableColumnResize &&
    (resizeEnabled ?? !!tag) &&
    (!!tag || !!onColumnResize);

  const columns = useMemo(() => {
    if (
      !tag ||
      onColumnResize ||
      columnWidths.tag !== tag ||
      columnWidths.viewType !== viewType
    )
      return sourceColumns;

    return sourceColumns.map((column) => {
      const resized = columnWidths.values.get(column?.valueField);
      if (
        !resized ||
        (column.width !== resized.sourceWidth && column.width !== resized.width)
      )
        return column;

      return { ...column, width: resized.width, widthPersisted: true };
    });
  }, [sourceColumns, columnWidths, tag, viewType, onColumnResize]);

  const commitColumnWidth = (column, width) => {
    if (onColumnResize) {
      onColumnResize(column, width);
      return;
    }
    if (!tag || !columnResizeEnabled) return;

    const updatedColumns = withUpdatedColumnWidth(
      columns,
      column.valueField,
      width
    );
    const payload = {
      tag,
      colunasAlteradas: buildWidthPayload(updatedColumns),
    };
    const saveKey = JSON.stringify({ viewType, ...payload });
    if (lastColumnWidthSaveRef.current?.key === saveKey) return;

    const request = { key: saveKey };
    lastColumnWidthSaveRef.current = request;

    const sourceColumn = sourceColumns.find(
      (item) => item?.valueField === column.valueField
    );
    setColumnWidths((previous) => {
      const values = new Map(
        previous.tag === tag && previous.viewType === viewType
          ? previous.values
          : []
      );
      values.set(column.valueField, {
        sourceWidth: sourceColumn?.width,
        width,
      });
      return { tag, viewType, values };
    });
    onColumnsChange && onColumnsChange(updatedColumns);

    pendingColumnWidthSaveRef.current = (
      pendingColumnWidthSaveRef.current || Promise.resolve()
    )
      .then(() =>
        httpSelector
          .getHttp(viewType)
          .post('/ColunasTabela/SalvarTamanhoColunas', payload)
      )
      .catch((error) => {
        if (lastColumnWidthSaveRef.current === request) {
          lastColumnWidthSaveRef.current = null;
        }
        errorHandler(error);
      });
  };

  const thRefs = useRef({});
  const resizeRef = useRef(null);
  const justResizedRef = useRef(false);

  useEffect(() => {
    if (select && onSelect) {
      onSelect([...(currentSelectedItems || [])]);
    }

    if (selectedCallback) selectedCallback([...(currentSelectedItems || [])]);
  }, [currentSelectedItems]);

  const handleColumnResizeMove = (event) => {
    const resize = resizeRef.current;
    if (!resize) return;

    if (Math.abs(event.clientX - resize.startX) > 3) resize.moved = true;

    const newWidth = computeResizedWidth({
      startWidth: resize.startWidth,
      startX: resize.startX,
      clientX: event.clientX,
      minWidth: resize.minWidth,
    });
    resize.currentWidth = newWidth;

    // Preview ao vivo direto no DOM: evita re-renderizar a tabela inteira a cada mousemove.
    const th = thRefs.current[resize.valueField];
    if (th) {
      th.style.width = `${newWidth}px`;
      th.style.minWidth = `${newWidth}px`;
      th.style.maxWidth = `${newWidth}px`;

      // As células do corpo precisam acompanhar e truncar com "...": sem isso, o texto
      // delas (nowrap) impede a coluna de encolher até a largura do cabeçalho.
      const table = th.closest('table');
      if (table) {
        table
          .querySelectorAll(`[data-resize-col="${resize.valueField}"]`)
          .forEach((cell) => applyCellTruncation(cell, newWidth));
      }
    }
  };

  const handleColumnResizeEnd = () => {
    document.removeEventListener('mousemove', handleColumnResizeMove);
    document.removeEventListener('mouseup', handleColumnResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    const resize = resizeRef.current;
    resizeRef.current = null;
    if (!resize) return;

    if (resize.moved && resize.currentWidth) {
      // O browser ainda despacha um click nativo após o mouseup, geralmente no próprio
      // <th> (não no handle) — esse guard evita que ele dispare handleHeaderClick.
      justResizedRef.current = true;
      setTimeout(() => {
        justResizedRef.current = false;
      }, 0);

      commitColumnWidth(resize.column, `${resize.currentWidth}px`);
    }
  };

  const handleColumnResizeStart = (event, column) => {
    event.preventDefault();
    event.stopPropagation();

    const th = thRefs.current[column.valueField];
    resizeRef.current = {
      valueField: column.valueField,
      column,
      startX: event.clientX,
      startWidth: th ? th.offsetWidth : parseInt(column.width, 10) || 150,
      minWidth: Math.max(
        MIN_COLUMN_WIDTH,
        Number(column.minWidth) || 0,
        measureHeaderMinWidth(th)
      ),
      currentWidth: null,
      moved: false,
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleColumnResizeMove);
    document.addEventListener('mouseup', handleColumnResizeEnd);
  };

  // Limpeza defensiva: caso a lista seja desmontada com um resize em andamento.
  useEffect(() => {
    return () => {
      if (resizeRef.current) {
        document.removeEventListener('mousemove', handleColumnResizeMove);
        document.removeEventListener('mouseup', handleColumnResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        resizeRef.current = null;
      }
    };
  }, []);

  const getCellPopoVer = (item, column) => {
    if (column.popoverFunction) {
      return column.popoverFunction(item, propsPopOver);
    }
  };

  const handleHeaderClick = (column) => {
    if (justResizedRef.current) return;
    if (!column.sortField) return;

    const sortByKey = sortBy === 'asc' ? 'desc' : 'asc';

    onSortColumn && onSortColumn(column.sortField, sortByKey);
    onSortByColumn && onSortByColumn(sortByKey);
  };

  const getHeaderText = (column) => {
    if (column.resource) {
      if (resources) {
        return resources[column.resource];
      } else {
        return column.customHeaderText ?? column.headerText;
      }
    } else {
      return column.customHeaderText ?? column.headerText;
    }
  };

  const getCellContent = (item, column) => {
    if (column.valueFunction) {
      return column.valueFunction(item, propsPopOver, column);
    }

    if (column.valueField) {
      let value = item[column.valueField];

      if (column.formatFunction) {
        value = column.formatFunction(value);
      }

      if (item.campoCustomizado) {
        if (item.campoCustomizado.length > 0) {
          let campoCustomizado = item.campoCustomizado.find(
            (i) => i.nomeCampo === column.valueField
          );
          if (campoCustomizado) value = campoCustomizado.valor;
        }
      }

      return value;
    }
  };

  const isUpdated = (item) => {
    return updatedItems.find((i) => i.id === item.id);
  };

  const handleDeleteClickConfirm = async (item, index) => {
    if (handleRemove) handleRemove && handleRemove(item, index);
    else
      try {
        await http.post(`${url}/Remove`, { id: item.id, parameters });
        ContentManager.addContent(
          <Alert
            title={<FormattedMessage id="sucesso" />}
            message={<FormattedMessage id="label.registroExcluidoComSucesso" />}
          />
        );
        onRefresh && onRefresh();
        onRemove && onRemove();
      } catch (error) {
        errorHandler(error);
      }
  };

  const handleDeleteClick = (item, index) => {
    if (onDeleting) {
      onDeleting(item);
    } else {
      ContentManager.addContent(
        <Confirm
          message={
            <FormattedMessage id="label.desejaMesmoExcluirEsseRegistro" />
          }
          handleConfirm={() => handleDeleteClickConfirm(item, index)}
        />
      );
    }
  };

  const renderMenu = (item, indexItem) => {
    return (
      <UncontrolledDropdown>
        <DropdownToggle
          color="default"
          size="sm"
          toolbar
          icon="EllipsisVertical"
        />
        <DropdownMenu>
          {item.permissions && (
            <DropdownItem
              onClick={() =>
                handleEditClick(item, indexItem, parameters, onRefresh)
              }
            >
              <LucideIcon icon="Pencil" />
              {intl.formatMessage(
                { id: idLabelEditar ? idLabelEditar : 'label.editar' },
                { editar: resources.editar }
              )}
            </DropdownItem>
          )}
          {item.permissions && item.permissions.allowDelete && showDelete && (
            <DropdownItem onClick={() => handleDeleteClick(item, indexItem)}>
              <LucideIcon icon="Trash2" />
              <FormattedMessage
                id={idLabelExcluir ? idLabelExcluir : 'excluir'}
              />
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  const checkCanSelected = (item) => {
    return !checkSelected || item.canSelected;
  };

  const handleToggleItem = (item) => {
    let newList = [];

    if (multiple) {
      newList = Object.assign([], currentSelectedItems);

      const index = newList.findIndex((x) => x.id === item.id);

      if (index === -1) newList.push(item);
      else newList.splice(index, 1);
    } else {
      newList.push(item);
    }

    setAllItensSelected(newList?.length === count);
    setCurrentSelectedItems(newList);
  };

  const handleToggleSelectAll = () => {
    if (!allItensSelected && data?.length > 0) {
      setCurrentSelectedItems([...data]);
    } else setCurrentSelectedItems([]);

    setAllItensSelected(!allItensSelected);
  };

  const isSelected = (id) => {
    return currentSelectedItems?.find((x) => x.id === id);
  };

  const renderFakeRows = (item, level) =>
    renderFakeChildrenWhileLoading(item.id, level * 20);

  const getStyleCell = (index) => {
    const stickyObj = {
      ...stickyDefault,
    };

    if (index === 0 && sticky) {
      stickyObj['left'] = 0;
      return stickyObj;
    }

    return {};
  };

  const getStyleCellBottom = (index) => {
    const stickyObj = {
      ...stickyDefault,
    };

    if (sticky) {
      if (index === 0) {
        stickyObj['zIndex'] = 6;
        stickyObj['left'] = 0;
      }

      stickyObj['bottom'] = 0;
      stickyObj['background'] = '#FFFFFF';
      stickyObj['fontWeight'] = 'bold';
      stickyObj['border'] = 'none';
      stickyObj['boxShadow'] = 'inset 0 2px 0 #dee2e6, inset 0 -2px 0 #dee2e6';
      return stickyObj;
    }

    return {};
  };

  // Colunas já redimensionadas (largura persistida) truncam o conteúdo com "...".
  const isTruncated = (column) =>
    columnResizeEnabled && column.resizable !== false && !!column.widthPersisted;

  const renderRows = (data, level = 0, parentColor = null) => {
    if (!data) return;

    return data.map((item, indexItem) => {
      // se for adm (usuario externo) nao tem 'permissions', então renderiza sempre o menu, se for interno, verifica se tem permissão de editar ou visualizar
      const canRenderRowMenu =
        !item?.permissions ||
        item.permissions?.allowEdit ||
        item.permissions?.allowView;
      const currentBackgroundColor =
        getRowColor && getRowColor(indexItem, parentColor);
      const currentTextColor =
        getRowTextColor && getRowTextColor(indexItem, parentColor);
      return [
        <tr
          key={item.id}
          style={{
            color: currentTextColor || 'inherit',
            backgroundColor: !currentBackgroundColor
              ? isUpdated(item)
                ? '#ECECEC'
                : '#FFFFFF'
              : currentBackgroundColor,
          }}
        >
          {showMenu && (
            <td style={{ zIndex: 1, position: 'relative' }}>
              {canRenderRowMenu &&
                (renderMenuProps
                  ? renderMenuProps(item, onRefresh)
                  : renderMenu(item, indexItem))}
            </td>
          )}
          {select && (
            <td style={{ width: 30 }}>
              {checkCanSelected(item) && (
                <Checkbox
                  id={`toggle-individual-item-${guid()}`}
                  key={guid()}
                  disabled={
                    selectLimit &&
                    currentSelectedItems?.length >= selectLimit &&
                    !isSelected(item.id)
                  }
                  checked={isSelected(item.id)}
                  onChange={() => handleToggleItem(item)}
                />
              )}
            </td>
          )}
          {columns.map(
            (column, index) =>
              column?.visible && (
                <td
                  id={'col' + column.valueField + item.id}
                  key={index}
                  data-resize-col={
                    columnResizeEnabled && column.resizable !== false
                      ? column.valueField
                      : undefined
                  }
                  className={classnames('results-table-cell', column.className)}
                  style={{
                    ...column.style,
                    ...getStyleCell(index),
                    ...(isTruncated(column)
                      ? {
                          width: column.width,
                          minWidth: column.width,
                          maxWidth: column.width,
                          overflow: 'hidden',
                        }
                      : {}),
                  }}
                >
                  <div
                    className={`d-flex m-auto align-items-center ${
                      !column.tree && showScrollColumn
                        ? 'overflow-y-auto overflow-x-hidden'
                        : !column.tree && !showScrollColumn
                        ? 'overflow-y-hidden overflow-x-hidden'
                        : ''
                    }`}
                    style={{
                      paddingLeft: column.tree ? level * 25 : 0,
                      maxHeight: column.maxHeight
                        ? column.maxHeight
                        : !column.tree
                        ? 130
                        : '',
                      minHeight: column.tree ? 25 : 'auto',
                      maxWidth: column.widthPersisted
                        ? column.width
                        : column.maxWidth
                        ? column.maxWidth
                        : 'auto',
                      minWidth: column.widthPersisted
                        ? column.width
                        : column.minWidth
                        ? column.minWidth
                        : 100,
                      wordWrap: 'anywhere',
                      ...(isTruncated(column) ? truncatedOuterStyle : {}),
                    }}
                  >
                    {column.tree &&
                      (item.hasChildren ? (
                        <>
                          {isExpanded(item) ? (
                            <ChevronDown
                              className="results-table-hierarchy"
                              color={colorIcon}
                              onClick={() => handleToggleExpand(item)}
                            />
                          ) : (
                            <ChevronRight
                              className="results-table-hierarchy"
                              color={colorIcon}
                              onClick={() => handleToggleExpand(item)}
                            />
                          )}
                        </>
                      ) : (
                        <span
                          className="results-table-hierarchy"
                          style={{ display: 'inline-block' }}
                        />
                      ))}
                    <div
                      style={{
                        maxHeight: column.maxHeight ? column.maxHeight : '',
                        ...(isTruncated(column) ? truncatedContentStyle : {}),
                      }}
                    >
                      {getCellContent(item, column)}
                      {getCellPopoVer(item, column)}
                    </div>
                  </div>
                </td>
              )
          )}
        </tr>,
        expandedIds &&
          expandedIds.indexOf(item.id) !== -1 &&
          ((loadingChildrenIds
            ? loadingChildrenIds.includes(item.id)
            : !item.children?.length) && renderFakeChildrenWhileLoading
            ? renderFakeRows(item, level + 1)
            : renderRows(item.children, level + 1, currentBackgroundColor)),
      ];
    });
  };

  const renderTotalRow = (dataTotalRow) => {
    if (!dataTotalRow) return;

    return columns.map(
      (column, index) =>
        column?.visible && (
          <td
            id={'col' + column.valueField + 't'}
            key={index}
            className={classnames('results-table-cell', column.className)}
            style={{ ...column.style, ...getStyleCellBottom(index) }}
          >
            <div
              className={`d-flex m-auto align-items-center ${
                !column.tree && showScrollColumn
                  ? 'overflow-y-auto overflow-x-hidden'
                  : !column.tree && !showScrollColumn
                  ? 'overflow-y-hidden overflow-x-hidden'
                  : ''
              }`}
              style={{
                paddingLeft: 0,
                maxHeight: column.maxHeight
                  ? column.maxHeight
                  : !column.tree
                  ? 130
                  : '',
                minHeight: column.tree ? 25 : 'auto',
                maxWidth: column.widthPersisted
                  ? column.width
                  : column.maxWidth
                  ? column.maxWidth
                  : 'auto',
                minWidth: column.widthPersisted
                  ? column.width
                  : column.minWidth
                  ? column.minWidth
                  : 100,
                wordWrap: 'anywhere',
              }}
            >
              <div
                style={{
                  maxHeight: column.maxHeight ? column.maxHeight : '',
                }}
              >
                {getCellContent(dataTotalRow, column)}
                {getCellPopoVer(dataTotalRow, column)}
              </div>
            </div>
          </td>
        )
    );
  };

  const renderSort = (column) => {
    if (!forceShowSort && (!sort || !column.sortField)) return;

    let SortIcon = ArrowUpDown;

    if (column.sortField == sort?.split(' ')[0] && sortBy == 'desc') {
      SortIcon = ChevronUp;
    }
    if (column.sortField == sort?.split(' ')[0] && sortBy == 'asc') {
      SortIcon = ChevronDown;
    }

    return (
      <SortIcon style={{ cursor: 'pointer' }} size={14} className="ml-2" />
    );
  };

  const getStyleHeader = (index) => {
    const stickyObj = {
      ...stickyDefault,
      verticalAlign: 'top',
    };

    if (sticky) {
      stickyObj['top'] = -1;
      stickyObj['background'] = '#FFFFFF';
      stickyObj['border'] = 'none';
      stickyObj['boxShadow'] = 'inset 0 2px 0 #dee2e6, inset 0 -2px 0 #dee2e6';

      if (index === 0) {
        stickyObj['zIndex'] = 6;
        stickyObj['left'] = 0;
        return stickyObj;
      }

      return stickyObj;
    }

    return { verticalAlign: 'top' };
  };

  const getHeaderStyle = (column, index, resizable) => {
    const baseStyle = { ...column.styleHead, ...getStyleHeader(index) };

    // O handle precisa de um ancestral posicionado desde o primeiro arraste (não só
    // depois que a coluna já tem widthPersisted), senão ele ancora em algum elemento
    // pai errado. Só força position:relative quando o handle vai de fato renderizar
    // aqui e a coluna ainda não tem uma position própria (ex. colunas sticky).
    const positioned =
      resizable && !baseStyle.position ? { position: 'relative' } : {};

    // Só mexe em width/minWidth/maxWidth quando a coluna foi de fato redimensionada
    // e persistida — caso contrário preserva exatamente o que já vem de styleHead/
    // getStyleHeader hoje (várias telas, ex. estatisticas/view.jsx, já usam
    // styleHead.minWidth e não podem ter esse valor pisado por um resize inexistente).
    if (!column.widthPersisted) return { ...baseStyle, ...positioned };

    return {
      ...baseStyle,
      ...positioned,
      width: column.width,
      minWidth: column.width,
      maxWidth: column.width,
    };
  };

  return (
    <div>
      {data && data.length > 0 ? (
        <Table
          drag={dragTable}
          responsive={responsive}
          styleDiv={{}}
          style={{ background: '#FFF', marginBottom: 0 }}
          count={count}
          stickyHeader={sticky}
        >
          <thead>
            <tr>
              {showMenu && (
                <th style={{ width: 30, ...getStyleHeader(-1) }}></th>
              )}
              {select && (
                <th style={{ width: 30, ...getStyleHeader(-1) }}>
                  {multiple && showSelectAll && (
                    <Checkbox
                      id="toggle-all"
                      disabled={selectLimit && count > selectLimit}
                      checked={allItensSelected}
                      onChange={handleToggleSelectAll}
                    />
                  )}
                </th>
              )}
              {columns.map(
                (column, index) =>
                  column?.visible && (
                    <th
                      ref={(el) => {
                        thRefs.current[column.valueField] = el;
                      }}
                      key={index}
                      onClick={() => handleHeaderClick(column)}
                      className={classnames(
                        column.className,
                        showFullTextColumns ? 'text-nowrap' : ''
                      )}
                      style={getHeaderStyle(
                        column,
                        index,
                        columnResizeEnabled && column.resizable !== false
                      )}
                    >
                      <div className="d-flex align-items-center">
                        {getHeaderText(column)}
                        {showSortIcon && column.sortField && renderSort(column)}
                        {column.tooltipHeader && (
                          <Info
                            style={{ marginLeft: 5 }}
                            id={`hint-column-list-${index}`}
                            tooltipMaxWidth={500}
                            text={column.tooltipHeader}
                          />
                        )}
                      </div>
                      {columnResizeEnabled && column.resizable !== false && (
                        <span
                          onMouseDown={(e) => handleColumnResizeStart(e, column)}
                          onClick={(e) => e.stopPropagation()}
                          // Mesma barra cinza ao passar o mouse que o List (Indicadores).
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              'rgba(0, 0, 0, 0.12)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: -4,
                            width: 8,
                            height: '100%',
                            cursor: 'col-resize',
                            zIndex: 1,
                            userSelect: 'none',
                          }}
                        />
                      )}
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody>
            {renderRows(data)}
            {dataTotalRow && <tr>{renderTotalRow(dataTotalRow)}</tr>}
            {isLoading &&
              renderContentWhileLoading &&
              renderContentWhileLoading()}
          </tbody>
        </Table>
      ) : (
        !isLoading && (
          <Table
            responsive={responsive}
            hover={false}
            styleDiv={{}}
            style={{ background: '#FFF', marginBottom: 0 }}
          >
            <tbody>
              <tr>
                <td style={{ borderTop: 0, padding: 0 }}>
                  <p style={{ fontStyle: 'italic', padding: 20 }}>
                    {intl.formatMessage({
                      id: 'label.nenhumRegistroFoiEncontrado',
                    })}
                  </p>
                </td>
              </tr>
            </tbody>
          </Table>
        )
      )}
      {showPagination && count > 0 && (
        <div className="d-flex justify-content-end p-3">
          <Pagination
            allowPaging
            currentPage={currentPage}
            count={count}
            pageSize={pageSize}
            changeCurrentPage={onChangeCurrentPage}
            changePageSize={onChangePageSize}
            onRefreshClick={handleRefreshPage}
          />
        </div>
      )}
    </div>
  );
}

export default Results;
